import {
  EventType,
  LogLevel,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser';

import { config } from '../env';

/**
 * The one MSAL instance for the app. Nothing else constructs a
 * PublicClientApplication — a second instance means a second token cache and
 * a sign-in that appears to succeed while the API client still has no token.
 */

export const msalConfig: Configuration = {
  auth: {
    clientId: config.entra_client_id,
    authority: config.entra_authority,
    redirectUri: config.redirect_uri,
    postLogoutRedirectUri: config.post_logout_redirect_uri,
    // The SPA is a *public* client. There is no client secret anywhere in this
    // codebase; MSAL uses authorization code + PKCE. navigateToLoginRequestUrl
    // is off so the redirect lands on a stable route rather than replaying a
    // deep link before React Router has mounted.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // sessionStorage, not localStorage: tokens die with the tab. For a tool
    // holding contract facts on shared and hot-desked machines that is the
    // right default, and ~12 users re-authenticating silently against an
    // existing Entra session is not a usability problem.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: config.app_env === 'prod' ? LogLevel.Warning : LogLevel.Info,
      // piiLoggingEnabled MUST stay false. MSAL's PII logs carry tokens,
      // UPNs and claim payloads; NFR-07 wants access logged server-side in
      // audit_log, not scattered through browser consoles.
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii || config.app_env === 'prod') return;
        // Deliberate: MSAL's own diagnostics, gated on non-prod and non-PII above.
        console.debug(`[msal:${LogLevel[level]}] ${message}`);
      },
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Scopes requested at sign-in.
 *
 * `openid`/`profile` produce the id token whose claims the shell displays.
 * The API scope is requested up front so the first API call does not trigger
 * a second interactive round trip.
 *
 * These are OAuth scopes. They are unrelated to CLM party/practice scope,
 * which the gateway derives from Entra group claims server-side. The client
 * never asks for, names or transmits a CLM scope (NFR-06).
 */
export const loginRequest: RedirectRequest = {
  scopes: ['openid', 'profile', config.api_scope],
};

/** Token request used by the API client for silent acquisition. */
export function apiTokenRequest(account: AccountInfo): SilentRequest {
  return { scopes: [config.api_scope], account };
}

export function getActiveAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

/**
 * Initialise MSAL and settle any redirect that is in flight. Must be awaited
 * before React renders: MsalProvider assumes an initialised instance, and
 * handleRedirectPromise must run before anything reads the account list or the
 * app will briefly render as signed-out immediately after a successful login.
 */
export async function initialiseAuth(): Promise<void> {
  await msalInstance.initialize();

  const result = await msalInstance.handleRedirectPromise();
  if (result?.account) {
    msalInstance.setActiveAccount(result.account);
  } else if (!msalInstance.getActiveAccount()) {
    const first = msalInstance.getAllAccounts()[0];
    if (first) msalInstance.setActiveAccount(first);
  }

  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS
    ) {
      const payload = event.payload as AuthenticationResult | null;
      if (payload?.account) msalInstance.setActiveAccount(payload.account);
    }
  });
}
